/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, April 6th 2022, 3:59:17 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

/**
 * The NestedOne2ManyListEditor is used when the data schema contains a list of items similar to
 * the NestedOne2ManyPopupFormInput. However, this components renders an editable list instead of
 * a popup form for each selected item. This input can be used for data that is not necessary posted
 * to an API.
 */

import React from "react";
import { FieldError } from "react-hook-form";
import { useControlledInput } from "ui-widgets/forms";
import { OSActionButton } from "ui-widgets";
import { APIFormRelatedPopupInputWrapper } from "ui-widgets/forms/input-fields/related/APIFormRelatedPopupInputWrapper";
import { FormInputCustomErrorWrapper } from "ui-widgets/forms/FormInputWrappers";
import { SimpleListEditor, EditListRenderPropSpec } from "ui-widgets/editors/SimpleListEditor";
import { APIRelatedObjectInputProps, OSFormInputProps } from "ui-widgets/forms/types";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TableContainer from "@mui/material/TableContainer";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { COLORSCHEME } from "theme/styles";
import { ObjectKey } from "lib/types";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";

type HeaderCellComponent = React.ReactNode;

type BodyCellComponent = React.ReactNode;

type TableHeaderCells<TRelatedObject> = Partial<{
  [K in ObjectKey<TRelatedObject>]: HeaderCellComponent;
}>;

type TableBodyCells<TRelatedObject> = Partial<{
  [K in ObjectKey<TRelatedObject>]: (
    item: TRelatedObject,
    onChangeRelatedItem: (item: TRelatedObject) => void
  ) => BodyCellComponent;
}>;

type NestedOne2ManyInlineListEditorProps<PrimaryObject, RelatedObject> = OSFormInputProps<PrimaryObject> &
  Omit<APIRelatedObjectInputProps<RelatedObject>, "getOptionLabel"> & {
    headerCells: TableHeaderCells<RelatedObject>;
    bodyCells: TableBodyCells<RelatedObject>;
    getNewItemDefaultValues: () => RelatedObject;
  };

export type ConcreteNestedOne2ManyInlineListEditorProps<PrimaryObject, RelatedObject> = Pick<
  NestedOne2ManyInlineListEditorProps<PrimaryObject, RelatedObject>,
  "schema_field_key" | "label"
>;

export const NestedOne2ManyInlineListEditor = <PrimaryObject, RelatedObject>(
  props: NestedOne2ManyInlineListEditorProps<PrimaryObject, RelatedObject>
) => {
  const {
    field: { onChange, value },
    fieldState: { error: primaryFieldError },
  } = useControlledInput<PrimaryObject>(props);

  // Get values on the first render and dont follow the controlled value after that. React will
  // complain about the missing dependency but its intentional
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialItems = React.useMemo(() => value, []);

  // Setting the form field value for this controlled input
  const handleOnUpdateList = React.useCallback((updatedItems: RelatedObject[]) => {
    onChange(updatedItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <APIFormRelatedPopupInputWrapper {...props}>
      <SimpleListEditor
        initialItems={(initialItems as unknown as RelatedObject[]) ?? []}
        onUpdateList={handleOnUpdateList}
      >
        {({ listState, addNewItem, updateList, selectItemToEdit, removeItem }) => {
          const onChangeRelatedItem = (item: RelatedObject) => {
            updateList(item);
          };

          const onClickAddNewItem = () => {
            addNewItem(props.getNewItemDefaultValues());
          };

          return (
            <>
              {props.label ? (
                <Grid item md={12}>
                  <Typography variant="h6" color={COLORSCHEME.primary}>
                    {props.label}
                  </Typography>
                </Grid>
              ) : null}

              <Grid item md={12}>
                <InlineListEditor<PrimaryObject, RelatedObject>
                  itemIdProperty={props.itemIdProperty}
                  listState={listState}
                  selectItemToEdit={selectItemToEdit}
                  removeItem={removeItem}
                  onChangeRelatedItem={onChangeRelatedItem}
                  headerCells={props.headerCells}
                  bodyCells={props.bodyCells}
                  primaryFieldError={primaryFieldError}
                />
              </Grid>
              <Grid item md={1}>
                <OSActionButton actionStyle="add" buttonType="floating" onClick={onClickAddNewItem} />
              </Grid>
            </>
          );
        }}
      </SimpleListEditor>
    </APIFormRelatedPopupInputWrapper>
  );
};

interface InlineListEditorProps<PrimaryObject, RelatedObject>
  extends Pick<
      NestedOne2ManyInlineListEditorProps<PrimaryObject, RelatedObject>,
      "headerCells" | "bodyCells" | "itemIdProperty"
    >,
    Pick<EditListRenderPropSpec<RelatedObject>, "listState" | "selectItemToEdit" | "removeItem"> {
  onChangeRelatedItem: (item: RelatedObject) => void;
  primaryFieldError: FieldError;
}

const InlineListEditor = <PrimaryObject, RelatedObject>({
  itemIdProperty,
  listState: { currentEditItemIndex, editableItems },
  selectItemToEdit,
  removeItem,
  headerCells,
  bodyCells,
  onChangeRelatedItem,
  primaryFieldError,
}: InlineListEditorProps<PrimaryObject, RelatedObject>) => {
  const fieldKeys = Object.keys(headerCells);
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {fieldKeys.map((fieldKey) => (
              <TableCell sx={{ borderBottom: "none" }} key={fieldKey}>
                {headerCells[fieldKey]}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {editableItems.map((item, itemIndex) => {
            const one2manyItemErrors = primaryFieldError?.[itemIndex] ?? null;

            return (
              <TableRow
                // selected={itemIndex === currentEditItemIndex}
                // hover={index !== currentEditItemIndex}
                key={`${item[itemIdProperty] ?? itemIndex}`}
              >
                {fieldKeys.map((fieldKey) => {
                  const bodyCellRenderProp = bodyCells[fieldKey as ObjectKey<RelatedObject>];

                  return (
                    <TableCell onClick={() => selectItemToEdit(itemIndex)} key={fieldKey}>
                      {bodyCellRenderProp(item, onChangeRelatedItem)}
                      {one2manyItemErrors?.[fieldKey]?.message ? (
                        <FormInputCustomErrorWrapper>
                          {one2manyItemErrors[fieldKey].message}
                        </FormInputCustomErrorWrapper>
                      ) : null}
                    </TableCell>
                  );
                })}
                <TableCell>
                  <IconButton edge="end" aria-label="delete" onClick={() => removeItem(itemIndex)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
